import clsx from 'clsx';
import React, { useRef } from 'react';
import styles from './ProductDashboardCard.module.scss';
import { ProductDashboardCardViewProps } from 'enums/ProductDashboardCardEnums';
import COButton from 'shared/components/COButton';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { toPng } from 'html-to-image';
import { Product } from 'models/Product/product.model';
import CarbonLabel from 'assets/images/carbonLabel.png';
import { ProductsEnums } from 'enums/ProductsEnums';
import formatNumberWithCommas from 'shared/utils/formatNumberWithComma';

interface ProductDashboardCardProps {
  annual?: boolean;
  goalAnnualProduction?: number | undefined;
  totalCarbonEmissionOfAnnualProduction?: number | undefined;
  labelValue?: number | undefined;
  handleOpenModal?: () => void;
  product?: Product | undefined;
}

const ProductDashboardCard: React.FC<ProductDashboardCardProps> = ({
  annual,
  goalAnnualProduction,
  totalCarbonEmissionOfAnnualProduction,
  handleOpenModal,
  labelValue,
  product,
}: ProductDashboardCardProps) => {
  const goalFinal = goalAnnualProduction
    ? Number(goalAnnualProduction.toFixed(2))
    : 0;
  const goalEstimate = Number(goalFinal).toFixed(2);

  let percentageDifference: number | undefined;
  if (annual && totalCarbonEmissionOfAnnualProduction !== undefined) {
    const difference = totalCarbonEmissionOfAnnualProduction - goalFinal;
    percentageDifference = Math.max(
      Number(((difference / goalFinal) * 100).toFixed(0))
    );
  }

  const carbonLabelRef = useRef<HTMLDivElement>(null);

  const htmlToImageConvert = () => {
    if (!carbonLabelRef.current) return;
    toPng(carbonLabelRef.current, { cacheBust: true })
      .then(dataUrl => {
        const link = document.createElement('a');
        link.download = `${product?.name}-carbon-footprint.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch(err => {
        console.log('Error occur while conversion ', err);
      });
  };

  const carbonValue = labelValue?.toFixed(4);
  const isLargeCrarbonValue = carbonValue && carbonValue.toString().length > 12;
  const emissionHeading =
    percentageDifference !== undefined
      ? `${Math.abs(percentageDifference)}% ${
          percentageDifference > 0 ? 'more' : 'less'
        } than set goal `
      : '';

  return (
    <>
      {annual && totalCarbonEmissionOfAnnualProduction ? (
        <div
          className={clsx({
            [styles.cardContainerProduct]:
              totalCarbonEmissionOfAnnualProduction <= goalFinal,
            [styles.cardContainerProductRed]:
              totalCarbonEmissionOfAnnualProduction > goalFinal,
          })}>
          <div className={styles.leftContainerProduct}>
            <h1>
              {formatNumberWithCommas(totalCarbonEmissionOfAnnualProduction)}
              <span>
                {ProductDashboardCardViewProps.CO}
                <sub>{ProductDashboardCardViewProps.SUBSCRIPT}</sub>
                {ProductDashboardCardViewProps.POST_SUBSCRIPT}
              </span>
            </h1>
            {goalFinal ? (
              <p onClick={handleOpenModal}>
                {emissionHeading}
                <span className={styles.goalValue}>
                  {goalEstimate}{' '}
                  {ProductDashboardCardViewProps.KG_CARBON_EMISSION}
                </span>
                <span className={styles.goalEdit}>
                  {ProductDashboardCardViewProps.EDIT_GOAL_LABEL}
                </span>
              </p>
            ) : (
              <p onClick={handleOpenModal}>
                <span className={styles.goalEdit}>
                  {ProductDashboardCardViewProps.SET_GOAL_LABEL}
                </span>
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.footprintContainer}>
          <div
            ref={carbonLabelRef}
            className={clsx({
              [styles.footprintImage]: true,
              [styles.large]: isLargeCrarbonValue,
            })}>
            <img src={CarbonLabel} />
            <div>
              <div>{formatNumberWithCommas(carbonValue)}</div>
              <div>
                kg CO<sub>2</sub>e
              </div>
              <div>per kg</div>
            </div>
          </div>
          <div>
            <COButton
              primary
              onClick={htmlToImageConvert}
              disabled={product?.status !== ProductsEnums.APPROVED}>
              Download <DownloadIcon />
            </COButton>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductDashboardCard;
